import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaHandshake, 
  FaMicrosoft, 
  FaCloud, 
  FaShieldAlt, 
  FaArrowRight,
  FaCheckCircle,
  FaUsers,
  FaGlobeAfrica,
  FaRocket,
  FaBuilding
} from 'react-icons/fa';

// Import partner logos
import advanta from '@assets/partners/Advanta-Africa.jpg';
import aws from '@assets/partners/AWS.png';
import twaweza from '@assets/partners/Twaweza_logo.png';

const partners = [
  {
    name: 'Microsoft',
    icon: FaMicrosoft,
    description: 'Gold Partner for Dynamics 365',
    type: 'Technology Partner',
  },
  {
    name: 'Safaricom',
    icon: FaCloud,
    description: 'Official MPESA Integration Partner',
    type: 'Integration Partner',
  },
  {
    name: 'Advanta Africa',
    icon: null,
    image: advanta,
    description: 'Strategic Business Partner',
    type: 'Strategic Partner',
  },
  {
    name: 'AWS',
    icon: null,
    image: aws,
    description: 'Cloud Infrastructure Partner',
    type: 'Cloud Partner',
  },
  {
    name: 'Visa',
    icon: FaShieldAlt,
    description: 'Payment Processing Partner',
    type: 'Payment Partner',
  },
  {
    name: 'Mastercard',
    icon: FaShieldAlt,
    description: 'Card Processing Partner',
    type: 'Payment Partner',
  },
  {
    name: 'Twaweza',
    icon: null,
    image: twaweza,
    description: 'Community Impact Partner',
    type: 'Community Partner',
  },
];

const partnershipBenefits = [
  {
    title: 'Technology Innovation',
    description: 'Access to cutting-edge technology and innovation',
    icon: FaRocket,
  },
  {
    title: 'Market Expansion',
    description: 'Reach new markets and customers across Africa',
    icon: FaGlobeAfrica,
  },
  {
    title: 'Revenue Growth',
    description: 'Generate new revenue streams and opportunities',
    icon: FaUsers,
  },
  {
    title: 'Strategic Support',
    description: 'Dedicated support and resources for success',
    icon: FaHandshake,
  },
];

const partnerStats = [
  { number: '15+', label: 'Strategic Partners' },
  { number: '200+', label: 'Joint Projects' },
  { number: '10+', label: 'Countries Reached' },
  { number: '100%', label: 'Partner Satisfaction' },
];

export default function Partners() {
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">
        
        {/* ─── Header ─── */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              Our Partners
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
              Strategic <span className="text-blue-600">Partners</span>
            </h1>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto text-base">
              We collaborate with industry leaders to deliver comprehensive solutions
            </p>
          </motion.div>
        </div>

        {/* ─── Partner Stats ─── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
          {partnerStats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              className="bg-white rounded-xl p-6 text-center border border-slate-200 hover:border-blue-300 transition-all hover:shadow-md"
            >
              <div className="text-3xl md:text-4xl font-bold text-blue-600">{stat.number}</div>
              <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* ─── Partner Cards ─── */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {partners.map((partner, index) => {
            const Icon = partner.icon;
            const hasImage = partner.image || partner.logo;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="group bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 text-center"
              >
                {/* Logo/Icon */}
                <div className="w-20 h-20 rounded-xl bg-slate-50 flex items-center justify-center mx-auto mb-4 group-hover:scale-105 transition-all duration-300 border border-slate-200">
                  {hasImage ? (
                    <img 
                      src={partner.image || partner.logo} 
                      alt={partner.name}
                      className="w-14 h-14 object-contain"
                    />
                  ) : Icon ? (
                    <Icon className="text-3xl text-slate-600" />
                  ) : (
                    <span className="text-2xl text-slate-600 font-bold">{partner.name.charAt(0)}</span>
                  )}
                </div>
                
                <h3 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                  {partner.name}
                </h3>
                <p className="text-sm text-slate-500 mt-1">{partner.description}</p>
                <div className="flex justify-center mt-3">
                  <span className="inline-block bg-slate-100 text-slate-600 text-xs px-3 py-1 rounded-full font-medium">
                    {partner.type}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* ─── Partnership Benefits ─── */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-6 bg-blue-600 rounded-full" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Why Partner With Us</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {partnershipBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-all shadow-sm hover:shadow-md text-center group"
                >
                  <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-all">
                    <Icon className="text-2xl text-blue-600" />
                  </div>
                  <h4 className="font-bold text-slate-900">{benefit.title}</h4>
                  <p className="text-sm text-slate-500 mt-1">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ─── Partner Success Story ─── */}
        <div className="mb-16 bg-white rounded-xl p-6 md:p-8 border border-slate-200 shadow-sm hover:shadow-md transition-all">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-6 bg-blue-600 rounded-full" />
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Partner Success</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <p className="text-slate-600 text-sm leading-relaxed">
                "Working with Micropoint Systems has been transformative for our business. 
                Their innovative solutions and dedicated support have helped us expand our 
                reach and deliver better value to our customers. Together, we're building 
                a stronger financial ecosystem across Africa."
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                  JD
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">John Doe</p>
                  <p className="text-xs text-slate-500">Partner Director, Advanta Africa</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2 text-blue-600">
                <FaCheckCircle className="text-xl" />
                <span className="font-semibold">Trusted Partnership</span>
              </div>
            </div>
          </div>
        </div>

        {/* ─── Become a Partner CTA ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative overflow-hidden rounded-xl p-8 md:p-10 text-center text-white"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.06)_0%,_transparent_70%)]" />
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-full bg-white/5 backdrop-blur-sm flex items-center justify-center border border-white/10">
                <FaHandshake className="text-2xl text-blue-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">Become a Partner</h2>
            </div>
            <p className="text-white/50 text-sm mb-6 max-w-lg mx-auto">
              Join our partner ecosystem and grow your business with Micropoint Systems. 
              Together, we can transform the financial technology landscape.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link 
                to="/contact" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold text-sm transition-all inline-flex items-center gap-2 hover:scale-105 shadow-lg hover:shadow-blue-500/20"
              >
                Partner With Us <FaArrowRight className="text-xs" />
              </Link>
              <Link 
                to="/about" 
                className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-full font-semibold text-sm transition-all inline-flex items-center gap-2 border border-white/10 hover:border-white/20"
              >
                Learn More
              </Link>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}